import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { DollarSign, Store, MessageSquare, Mail, KeyRound, User } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { LoadingSpinner } from '../../ui/Loading';
import {
  QUERY_KEYS,
  useUsdWalletSummary,
  useSuspendAdminShop,
  useUnsuspendAdminShop,
  useTerminateAdminShop,
  useAdminShopDetailByShopId,
  useAdminAgentDetail,
  useSuperAgentDetail,
  usePatchAdminShopPlayerWithdrawalAutoApprove,
} from '../../../hooks/useApi';
import { usePermission, useRole } from '../../../hooks/useAuth';
import FinanceCorrectionDebitModal from '../../finance/FinanceCorrectionDebitModal';
import OwnerWalletPnlCards from '../wallets/OwnerWalletPnlCards';
import EntityHierarchyChart from './EntityHierarchyChart';
import AdminUpdateContactModal from '../../shared/AdminUpdateContactModal';
import AdminSetPasswordModal from '../../shared/AdminSetPasswordModal';
import AdminUpdateUsernameModal from '../../shared/AdminUpdateUsernameModal';
import AdminPlayerWithdrawalAutoApproveCard from '../../shared/AdminPlayerWithdrawalAutoApproveCard';
import { costAsPercent } from './agentCdnUtils';
import type { AdminShopDetailPlayer } from '../../../types';
import { formatCurrency } from '../../../utils';
import { useBreadcrumbLabel } from '../../../contexts/BreadcrumbLabelContext';
import { apiClient } from '../../../services/api';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Separator,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@/components/ui/shadcn';

const SUSPEND_CONFIRM_WORD = 'suspended';
const TERMINATE_CONFIRM_WORD = 'terminate';

function formatPlayerBalance(p: AdminShopDetailPlayer): string {
  if (!p.wallet) return '—';
  return String(p.wallet.balance);
}

const ShopDetailPage: React.FC = () => {
  const { shopId } = useParams<{ shopId: string }>();
  const queryClient = useQueryClient();
  const canManage = usePermission('agentManagement');
  // KIOSK-720: Adjust Wallet is a write action — operation_admin/platform_admin may only VIEW wallets.
  const canAdjustWalletRole = useRole(['system_admin', 'super_admin', 'finance_admin']);
  const canFinanceCorrect = usePermission('walletManagement') && canAdjustWalletRole;
  const canUpdateContact = useRole(['system_admin', 'super_admin']);
  const canManageCredentials = canUpdateContact;
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [usernameModalOpen, setUsernameModalOpen] = useState(false);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [terminateOpen, setTerminateOpen] = useState(false);
  const [suspendConfirmText, setSuspendConfirmText] = useState('');
  const [terminateConfirmText, setTerminateConfirmText] = useState('');
  const [messagingSaving, setMessagingSaving] = useState(false);

  const suspendShop = useSuspendAdminShop();
  const unsuspendShop = useUnsuspendAdminShop();
  const terminateShop = useTerminateAdminShop();
  const patchPlayerAutoApprove = usePatchAdminShopPlayerWithdrawalAutoApprove();

  const {
    data: shopDetailResponse,
    isLoading,
    error,
  } = useAdminShopDetailByShopId(shopId, canManage);
  const {
    data: usdWalletSummary,
    isLoading: usdWalletLoading,
    error: usdWalletError,
  } = useUsdWalletSummary(
    { ownerType: 'shop', ownerId: shopId ?? '', currency: 'USD' },
    canManage && !!shopId,
  );

  const agentId = shopDetailResponse?.data?.agent_id ?? shopDetailResponse?.data?.shop?.agentId ?? undefined;
  const shop = shopDetailResponse?.data?.shop;
  const messagingEnabled = shop?.messagingEnabled !== false;
  useBreadcrumbLabel(shop?.name);
  const shopWallet = shopDetailResponse?.data?.shop_wallet;
  const players = useMemo(() => shopDetailResponse?.data?.players ?? [], [shopDetailResponse]);

  const { data: agentDetail, isLoading: agentLoading } = useAdminAgentDetail(agentId, canManage && !!agentId);
  const parentAgent = agentDetail?.data?.agent;
  const superAgentId = parentAgent?.superAgentId ?? undefined;
  const { data: superAgentDetail, isLoading: superAgentLoading } = useSuperAgentDetail(
    superAgentId,
    canManage && !!superAgentId,
  );
  const suspendPhraseOk = suspendConfirmText.trim().toLowerCase() === SUSPEND_CONFIRM_WORD;
  const terminatePhraseOk = terminateConfirmText.trim().toLowerCase() === TERMINATE_CONFIRM_WORD;

  const runSuspend = async () => {
    if (!agentId || !shopId || !suspendPhraseOk) return;
    try {
      await suspendShop.mutateAsync({ agentId, shopId });
      setSuspendOpen(false);
      setSuspendConfirmText('');
    } catch {
      // toast from hook
    }
  };

  const runTerminate = async () => {
    if (!agentId || !shopId || !terminatePhraseOk) return;
    try {
      await terminateShop.mutateAsync({ agentId, shopId });
      setTerminateOpen(false);
      setTerminateConfirmText('');
    } catch {
      // toast from hook
    }
  };

  const toggleShopMessaging = async () => {
    if (!shopId || !canManage) return;
    setMessagingSaving(true);
    try {
      await apiClient.patchAdminShopMessaging(shopId, !messagingEnabled);
      await queryClient.invalidateQueries(['adminShopDetailByShopId', shopId]);
      toast.success(!messagingEnabled ? 'Messaging enabled for shop' : 'Messaging disabled for shop');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Failed to update messaging');
    } finally {
      setMessagingSaving(false);
    }
  };

  if (!shopId) {
    return <div className="p-6 text-muted-foreground">Invalid URL.</div>;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !shop) {
    return (
      <div className="space-y-4">
        <p className="text-destructive">Shop not found or failed to load.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-3">
            <Store className="h-8 w-8 text-primary shrink-0" aria-hidden />
            <h1 className="text-2xl font-semibold tracking-tight text-foreground truncate">
              {shop.name}
            </h1>
          </div>
          <p className="text-sm text-muted-foreground">
            {shop.email}
            {shop.phone ? (
              <>
                <span className="mx-2 text-border">·</span>
                <span className="tabular-nums">{shop.phone}</span>
              </>
            ) : null}
          </p>
        </div>
        {canManage && (canFinanceCorrect || canUpdateContact || canManageCredentials) && shop.status !== 'terminated' ? (
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end shrink-0 w-full sm:w-auto">
            {canManageCredentials && agentId && shopId ? (
              <>
                <Button variant="outline" size="sm" className="w-full sm:w-auto justify-center" onClick={() => setUsernameModalOpen(true)}>
                  <User className="h-4 w-4 mr-1.5 shrink-0" />
                  Change username
                </Button>
                <Button variant="outline" size="sm" className="w-full sm:w-auto justify-center" onClick={() => setPasswordModalOpen(true)}>
                  <KeyRound className="h-4 w-4 mr-1.5 shrink-0" />
                  Change password
                </Button>
              </>
            ) : null}
            {canUpdateContact && agentId ? (
              <Button variant="outline" size="sm" className="w-full sm:w-auto justify-center" onClick={() => setContactModalOpen(true)}>
                <Mail className="h-4 w-4 mr-1.5 shrink-0" />
                Update email / phone
              </Button>
            ) : null}
            {canFinanceCorrect ? (
              <Button variant="outline" size="sm" className="w-full sm:w-auto justify-center border-destructive/50 text-destructive" onClick={() => setCorrectionOpen(true)}>
                Debit correction
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      <Separator />

      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Shop</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Status</span>
              <span className="font-medium uppercase tracking-wide">{shop.status}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Cost rate</span>
              <span className="font-medium">{costAsPercent(shop)}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Phone</span>
              <span className="font-medium">{shop.phone || '—'}</span>
            </div>
            {canManage ? (
              <div className="flex justify-between items-center gap-4 border-t pt-3 mt-2">
                <span className="text-muted-foreground inline-flex items-center gap-1">
                  <MessageSquare className="h-4 w-4" />
                  Player messaging
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={messagingSaving || shop.status === 'terminated'}
                  onClick={() => void toggleShopMessaging()}
                >
                  {messagingEnabled ? 'Enabled — disable' : 'Disabled — enable'}
                </Button>
              </div>
            ) : null}
            {canManage && agentId && shop.status !== 'terminated' ? (
              <div className="flex flex-col gap-2 border-t pt-3 mt-2">
                <p className="text-xs font-medium text-muted-foreground">Governance</p>
                <div className="flex flex-wrap gap-2">
                  {shop.status === 'active' ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-amber-600/50 text-amber-900 dark:text-amber-100"
                      onClick={() => {
                        setSuspendConfirmText('');
                        setSuspendOpen(true);
                      }}
                      disabled={suspendShop.isLoading || unsuspendShop.isLoading || terminateShop.isLoading}
                    >
                      Suspend shop
                    </Button>
                  ) : null}
                  {shop.status === 'suspended' ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (!window.confirm('Reactivate this suspended shop?')) return;
                        void unsuspendShop.mutateAsync({ agentId, shopId: shopId! });
                      }}
                      disabled={suspendShop.isLoading || unsuspendShop.isLoading || terminateShop.isLoading}
                    >
                      Unsuspend shop
                    </Button>
                  ) : null}
                  {shop.status === 'active' || shop.status === 'suspended' ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-destructive/50 text-destructive"
                      onClick={() => {
                        setTerminateConfirmText('');
                        setTerminateOpen(true);
                      }}
                      disabled={suspendShop.isLoading || unsuspendShop.isLoading || terminateShop.isLoading}
                    >
                      Terminate shop
                    </Button>
                  ) : null}
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">System credit</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {shopWallet ? (
              <>
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Balance</span>
                  <span className="font-mono font-medium">{shopWallet.balance}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Reserved</span>
                  <span className="font-mono font-medium">{shopWallet.reserved_balance}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Wallet status</span>
                  <span className="font-medium uppercase tracking-wide">{shopWallet.status}</span>
                </div>
              </>
            ) : (
              <p className="text-muted-foreground">No wallet.</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 space-y-2">
            <div className="flex items-center gap-2 text-muted-foreground">
              <DollarSign className="h-4 w-4 text-emerald-600" />
              <span className="text-sm font-medium">USD wallet</span>
            </div>
            {usdWalletLoading ? (
              <div className="flex justify-center py-3">
                <LoadingSpinner />
              </div>
            ) : usdWalletError ? (
              <p className="text-sm text-destructive">Could not load USD balance.</p>
            ) : (
              <>
                <p className="text-2xl font-semibold tabular-nums">
                  {formatCurrency(Number(usdWalletSummary?.balanceNet ?? 0))}{' '}
                  <span className="text-sm font-normal text-muted-foreground">net</span>
                </p>
                {Number(usdWalletSummary?.balanceGross ?? 0) !==
                Number(usdWalletSummary?.balanceNet ?? 0) ? (
                  <p className="text-xs text-muted-foreground tabular-nums">
                    Gross {formatCurrency(Number(usdWalletSummary?.balanceGross ?? 0))}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">Online settlement balance</p>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {canManage && shop.status !== 'terminated' && agentId && shopId ? (
        <AdminPlayerWithdrawalAutoApproveCard
          level="shop"
          snapshot={shop.playerWithdrawalAutoApprove}
          saving={patchPlayerAutoApprove.isLoading}
          onSave={async (value) => {
            await patchPlayerAutoApprove.mutateAsync({
              agentId,
              shopId,
              withdrawal_auto_approve_threshold: value,
            });
          }}
        />
      ) : null}

      {shopId ? <OwnerWalletPnlCards ownerType="shop" ownerId={shopId} enabled /> : null}

      <EntityHierarchyChart
        subtitle="Upstream parent chain for this shop"
        focus="shop"
        focusedShopId={shop.id}
        loadingParent={(!!agentId && agentLoading) || (!!superAgentId && superAgentLoading)}
        superAgent={
          superAgentDetail?.data?.superAgent
            ? {
                id: superAgentDetail.data.superAgent.id,
                name: superAgentDetail.data.superAgent.name,
                email: superAgentDetail.data.superAgent.email,
                status: superAgentDetail.data.superAgent.status,
                to: `/super-agents/${superAgentDetail.data.superAgent.id}`,
              }
            : superAgentId
              ? { id: superAgentId, name: 'Super agent', to: `/super-agents/${superAgentId}` }
              : null
        }
        agent={
          parentAgent
            ? {
                id: parentAgent.id,
                name: parentAgent.name,
                email: parentAgent.email,
                status: parentAgent.status,
                to: `/agents/${parentAgent.id}`,
              }
            : agentId
              ? { id: agentId, name: 'Agent', to: `/agents/${agentId}` }
              : null
        }
        shops={[
          {
            id: shop.id,
            name: shop.name,
            email: shop.email,
            status: shop.status,
            meta: `Cost ${costAsPercent(shop)}`,
          },
        ]}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Players ({players.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Username</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">CDN balance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {players.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                    No players assigned to this shop.
                  </TableCell>
                </TableRow>
              ) : (
                players.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">{p.username || '—'}</TableCell>
                    <TableCell>{p.email || '—'}</TableCell>
                    <TableCell className="tabular-nums">{p.phone?.trim() || '—'}</TableCell>
                    <TableCell>
                      {p.status ? <span className="uppercase tracking-wide">{p.status}</span> : '—'}
                    </TableCell>
                    <TableCell className="text-right font-mono">{formatPlayerBalance(p)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <FinanceCorrectionDebitModal
        open={correctionOpen}
        onOpenChange={setCorrectionOpen}
        ownerType="shop"
        ownerId={shopId!}
        onApplied={() => {
          void queryClient.invalidateQueries([QUERY_KEYS.ADMIN_SHOP_DETAIL_BY_SHOP_ID, shopId]);
          void queryClient.invalidateQueries({ queryKey: ['usdWalletSummary'] });
        }}
      />

      <Dialog open={suspendOpen} onOpenChange={(open) => !suspendShop.isLoading && setSuspendOpen(open)}>
        <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => suspendShop.isLoading && e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Suspend shop</DialogTitle>
            <DialogDescription>
              Suspending <span className="font-medium text-foreground">{shop.name}</span> blocks the shop from normal
              operation. Type <span className="font-mono">{SUSPEND_CONFIRM_WORD}</span> to confirm.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="shop-suspend-confirm">Confirmation</Label>
            <Input
              id="shop-suspend-confirm"
              value={suspendConfirmText}
              onChange={(e) => setSuspendConfirmText(e.target.value)}
              autoComplete="off"
              disabled={suspendShop.isLoading}
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setSuspendOpen(false)} disabled={suspendShop.isLoading}>
              Cancel
            </Button>
            <Button type="button" onClick={() => void runSuspend()} disabled={suspendShop.isLoading || !suspendPhraseOk}>
              {suspendShop.isLoading ? 'Working…' : 'Suspend shop'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={terminateOpen} onOpenChange={(open) => !terminateShop.isLoading && setTerminateOpen(open)}>
        <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => terminateShop.isLoading && e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Terminate shop</DialogTitle>
            <DialogDescription>
              Terminating <span className="font-medium text-foreground">{shop.name}</span> is permanent for this shop
              account. Type <span className="font-mono">{TERMINATE_CONFIRM_WORD}</span> to confirm.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="shop-terminate-confirm">Confirmation</Label>
            <Input
              id="shop-terminate-confirm"
              value={terminateConfirmText}
              onChange={(e) => setTerminateConfirmText(e.target.value)}
              autoComplete="off"
              disabled={terminateShop.isLoading}
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setTerminateOpen(false)} disabled={terminateShop.isLoading}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => void runTerminate()}
              disabled={terminateShop.isLoading || !terminatePhraseOk}
            >
              {terminateShop.isLoading ? 'Working…' : 'Terminate shop'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {canUpdateContact && agentId && shopId && shop.status !== 'terminated' ? (
        <AdminUpdateContactModal
          open={contactModalOpen}
          onClose={() => setContactModalOpen(false)}
          target={{ kind: 'shop', agentId, shopId }}
          currentEmail={shop.email}
          currentPhone={shop.phone}
          emailVerified={Boolean(
            (shop as { emailVerified?: boolean; email_verified?: boolean }).emailVerified ??
              (shop as { email_verified?: boolean }).email_verified
          )}
          phoneVerified={Boolean(
            (shop as { phoneVerified?: boolean; phone_verified?: boolean }).phoneVerified ??
              (shop as { phone_verified?: boolean }).phone_verified
          )}
          title="Update shop email / phone"
          onSuccess={() => {
            toast.success('Contact updated. Shop must re-verify.');
            void queryClient.invalidateQueries([QUERY_KEYS.ADMIN_SHOP_DETAIL_BY_SHOP_ID, shopId]);
            void queryClient.invalidateQueries([QUERY_KEYS.ADMIN_SHOP_DETAIL, agentId, shopId]);
            void queryClient.invalidateQueries([QUERY_KEYS.ADMIN_AGENT_DETAIL, agentId]);
          }}
        />
      ) : null}
      {canManageCredentials && agentId && shopId && shop.status !== 'terminated' ? (
        <>
          <AdminSetPasswordModal
            open={passwordModalOpen}
            onClose={() => setPasswordModalOpen(false)}
            target={{ kind: 'shop', agentId, shopId }}
            minLength={6}
            title="Change shop password"
            onSuccess={() => {
              toast.success('Shop password updated. Sessions were signed out.');
            }}
          />
          <AdminUpdateUsernameModal
            open={usernameModalOpen}
            onClose={() => setUsernameModalOpen(false)}
            target={{ kind: 'shop', agentId, shopId }}
            currentUsername={(shop as { username?: string | null }).username}
            title="Change shop username"
            onSuccess={() => {
              toast.success('Shop username updated. Sessions were signed out.');
              void queryClient.invalidateQueries([QUERY_KEYS.ADMIN_SHOP_DETAIL_BY_SHOP_ID, shopId]);
              void queryClient.invalidateQueries([QUERY_KEYS.ADMIN_SHOP_DETAIL, agentId, shopId]);
              void queryClient.invalidateQueries([QUERY_KEYS.ADMIN_AGENT_DETAIL, agentId]);
            }}
          />
        </>
      ) : null}
    </div>
  );
};

export default ShopDetailPage;
